import { useState } from "react";
import { copyrightRegistrations } from "../../../mocks/legal";

interface RegistrationItem {
  id: string;
  title: string;
  artist: string;
  registrationNo: string;
  registeredDate: string;
  status: string;
  royaltyEarned: number;
}

export default function CopyrightRegistry() {
  const [registrations, setRegistrations] = useState<RegistrationItem[]>(copyrightRegistrations);
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newArtist, setNewArtist] = useState("");

  const handleRegister = (id: string) => {
    setRegisteringId(id);
    setTimeout(() => {
      const newNo = `C-2026-KAP-${String(registrations.length + 1).padStart(3, "0")}`;
      setRegistrations((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status: "등록 완료", registrationNo: newNo, registeredDate: "2026.03.27" }
            : r
        )
      );
      setRegisteringId(null);
      setNotification("저작권 등록이 완료되었습니다. 음원 수익이 자동 집계됩니다.");
      setTimeout(() => setNotification(null), 3500);
    }, 2000);
  };

  const handleNewSubmit = () => {
    if (!newTitle.trim() || !newArtist.trim()) return;
    const newItem: RegistrationItem = {
      id: `cr-${Date.now()}`,
      title: newTitle,
      artist: newArtist,
      registrationNo: "대기중",
      registeredDate: "-",
      status: "신청 대기",
      royaltyEarned: 0,
    };
    setRegistrations((prev) => [...prev, newItem]);
    setNewTitle("");
    setNewArtist("");
    setShowNewForm(false);
  };

  const totalRoyalty = registrations.filter(r => r.status === "등록 완료").reduce((s, r) => s + r.royaltyEarned, 0);

  return (
    <div className="space-y-4">
      {notification && (
        <div className="bg-[#00C896]/10 border border-[#00C896]/30 rounded-xl px-4 py-3 text-[#00C896] text-sm flex items-center gap-2">
          <i className="ri-check-double-line text-lg"></i>{notification}
        </div>
      )}

      {/* Royalty Summary */}
      <div className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-5 flex items-center justify-between">
        <div>
          <div className="text-[#5A5E72] text-xs mb-1">총 누적 음원 수익 (등록 완료 기준)</div>
          <div className="text-[#00C896] text-2xl font-bold">{totalRoyalty.toLocaleString()}만원</div>
        </div>
        <button
          type="button"
          onClick={() => setShowNewForm(!showNewForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#E8375F] to-[#FF4B7A] text-white text-sm font-semibold rounded-xl cursor-pointer hover:shadow-lg hover:shadow-[#E8375F]/20 transition-all whitespace-nowrap"
        >
          <i className="ri-add-line text-base"></i>신규 등록 신청
        </button>
      </div>

      {/* New Form */}
      {showNewForm && (
        <div className="bg-[#1C1F2E] rounded-xl border border-[#E8375F]/40 p-5">
          <div className="text-[#111111] font-semibold text-sm mb-4">저작권 신규 등록 신청</div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[#5A5E72] text-xs mb-1.5 block">곡명 / 앨범명</label>
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="예: 루나&스타즈 - 'Summer Night'"
                className="w-full bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder:text-[#3E4155]"
              />
            </div>
            <div>
              <label className="text-[#5A5E72] text-xs mb-1.5 block">아티스트명</label>
              <input
                value={newArtist}
                onChange={(e) => setNewArtist(e.target.value)}
                placeholder="예: 루나&스타즈"
                className="w-full bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder:text-[#3E4155]"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleNewSubmit} className="flex-1 py-2.5 bg-[#E8375F] text-white text-sm font-semibold rounded-lg hover:bg-[#FF4B7A] cursor-pointer whitespace-nowrap">
              등록 신청
            </button>
            <button type="button" onClick={() => setShowNewForm(false)} className="px-4 py-2.5 bg-[#252830] text-[#8B91A8] text-sm rounded-lg hover:text-white cursor-pointer whitespace-nowrap">
              취소
            </button>
          </div>
        </div>
      )}

      {/* Registration List */}
      <div className="space-y-3">
        {registrations.map((r) => (
          <div key={r.id} className="bg-[#1C1F2E] rounded-xl border border-[#2A2D3E] p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-[#111111] font-semibold text-sm">{r.title}</div>
                <div className="text-[#5A5E72] text-xs mt-0.5">{r.artist}</div>
                <div className="flex items-center gap-4 mt-3 text-xs">
                  <div>
                    <span className="text-[#3E4155]">등록번호 </span>
                    <span className="text-[#111111] font-mono">{r.registrationNo}</span>
                  </div>
                  <div>
                    <span className="text-[#3E4155]">등록일 </span>
                    <span className="text-[#111111]">{r.registeredDate}</span>
                  </div>
                  {r.royaltyEarned > 0 && (
                    <div>
                      <span className="text-[#3E4155]">누적 수익 </span>
                      <span className="text-[#00C896] font-bold">{r.royaltyEarned.toLocaleString()}만원</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-2">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                  r.status === "등록 완료"
                    ? "text-[#00C896] bg-[#00C896]/10 border-[#00C896]/30"
                    : "text-[#FF9F43] bg-[#FF9F43]/10 border-[#FF9F43]/30"
                }`}>{r.status}</span>
                {r.status === "신청 대기" && (
                  <button
                    type="button"
                    onClick={() => handleRegister(r.id)}
                    disabled={registeringId === r.id}
                    className="px-3 py-1.5 bg-[#252830] hover:bg-[#2A2D3E] text-[#555555] text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {registeringId === r.id
                      ? <><i className="ri-loader-4-line animate-spin mr-1"></i>처리중</>
                      : <><i className="ri-file-add-line mr-1"></i>가상 저작권 등록</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
